import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications, SimpleModal } from '@sequencehq/core-components'
import { quoteAdapters } from 'Cube/communication/external/quotes.api.v1/adapters/quote.adapters'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'

/**
 * Displays a modal which allows for revoking a quote.
 */
export const RevokeQuoteModal = (props: {
  quoteId: string
  isOpen?: boolean
  onClose?: () => void
  onSuccess?: () => void
}) => {
  const hook = useRevokeQuoteModal({
    quoteId: props.quoteId,
    onSuccess: props.onSuccess
  })

  return (
    <SimpleModal
      variant="dialog"
      data-testid="revokeQuoteModal"
      isOpen={Boolean(props.isOpen)}
      onClose={() => props.onClose?.()}
      title="Remove quote access"
      submitButtonText="Remove access"
      submittingButtonText="Removing access..."
      isDestructive
      disableSubmitOnEnter
      onSubmit={hook.revoke}
    >
      Removing access will expire this quote. You can still republish this quote
      afterwards.
    </SimpleModal>
  )
}

const useRevokeQuoteModal = (props: {
  quoteId: string
  onSuccess?: () => void
}) => {
  const cubeContext = useCubeContext()

  const { displayNotification } = useNotifications()
  const quoteRes = useQuery(
    dashboard20240730Client.getQuote,
    { id: props.quoteId },
    {
      select: data => {
        if (!data) {
          return null
        }
        return {
          quoteNumber: data.quoteNumber
        }
      }
    }
  )

  const putQuoteMutator = useMutation(dashboard20240730Client.putQuote)

  const publishQuoteMutator = useMutation(
    dashboard20240730Client.postQuotePublish
  )

  const onError = () => {
    displayNotification('Could not remove access to quote', {
      type: 'error'
    })
    return false
  }

  return {
    revoke: async () => {
      const prevExpiresIn = cubeContext.queries.rawData.data.quote.expiresIn

      /**
       * Set quote `expiryDays` to -1 days
       */
      const dataToSave = quoteAdapters.out(cubeContext.queries.rawData.data)

      const putQuoteResult = await putQuoteMutator.mutateAsync({
        id: props.quoteId,
        body: {
          ...dataToSave,
          expiryDays: -1
        }
      })

      if (!putQuoteResult) {
        return onError()
      }

      /**
       * Republish quote
       */
      const republishQuoteResult = await publishQuoteMutator.mutateAsync({
        id: props.quoteId,
        body: {
          sendRecipientsEmail: false,
          emailMessage: undefined,
          makeReadyToSign: false,
          messageForApprover: undefined
        }
      })

      if (!republishQuoteResult) {
        return onError()
      }

      cubeContext.mutators.updateData({
        quote: {
          expiresAt: undefined,
          expiresIn: prevExpiresIn,
          isExpired: true
        }
      })

      props.onSuccess?.()

      displayNotification(
        `Access to quote ${quoteRes.data?.quoteNumber} removed`,
        { type: 'success' }
      )
      return true
    }
  }
}
